import React, { useState } from 'react';
import { Music, Search, Plus, Edit2, Trash2, Eye, Star, Heart, TrendingUp, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { hasPermission, formatDate } from '../../utils/helpers';
import { notifyNewFihirana } from '../../utils/notification-helpers';
import Button from '../common/Button';
import Modal from '../common/Modal';
import FihiranaViewer from './FihiranaViewer';

const FIHIRANA_CATEGORIES = [
  '03 – Ny Fanahy Masina',
  '04 – Ny telo izay iray',
  '05 – Ny soratra masina, tenin\'Andriamanitra',
  '07 – Ny Fiainanana Kristiana',
  '09 – Ny fivorian\'ny tanora',
  '1.01 – Ny fiderana sy fisaorana azy',
  '1.02 – Ny asany amin\'ny famoronana',
  '1.03 – Ny Fahatsarany amin\'ny fitondrana',
  '1.04 – Ny fahasoavany amin\'ny fanavotana',
  '10 – Ny hiran\'ny ankizy',
  '11 – Litania',
  '12 – Tsanta',
  '13 – Antema',
  '2.01 – Ny fihaviany sy ny fiverenany',
  '2.02 – Ny fahaterahany',
  '2.03 – Ny fiainany',
  '2.04 – Ny fijaliany – ny fahafatesany',
  '2.05 – Ny fitsanganany',
  '2.06 – Ny fiakarany',
  '2.07 – Ny fifonany ho antsika',
  '2.08 – Ny fanatrehany',
  '2.09 – Ny fiderana sy fisaorana azy',
  '6.01 – Fanombohana sy fiantsoana',
  '6.02 – Ny Sakramentan\'ny batisa',
  '6.03 – Ny Sakramentan\'ny Fanasan\'ny Tompo',
  '6.04 – Ny firaisan\'ny mino sy ny fiarahany mivavaka',
  '6.05 – Fanateram-bokatra',
  '6.06 – Fanokanana mpitandrina, pastora, mpiasa-piangonana',
  '6.07 – Fandraisana ho mpandray',
  '6.08 – Fampielezana ny filazantsara',
  '6.09 – Fitokanana trano fiangonana',
  '6.10 – Fisaorana an\'Andriamanitra noho ny mpiasany',
  '6.11 – Firavana',
  '7.01 – Ny Antson\'ny filazantsara',
  '7.02 – Ny fibebahana',
  '7.03 – Ny famelan-keloka – ny famonjena',
  '7.04 – Ny fanolorantena – ny fankatoavana- ny fanamasinana',
  '7.05 – Ny fiovam-po – ny fiainam-baovao',
  '7.06 – Ny fiainana ao amin\'ny Kristy',
  '7.07 – Fanantenana',
  '7.08 – Finoana',
  '7.09 – Fitiavana',
  '7.10 – Fiadanana sy fifaliana',
  '7.11 – Zava-tsarotra mahazo ny kristiana sy fampaherezana',
  '7.12 – Fitarainana amin\'ny Mpamonjy',
  '7.13 – Fangataham-pitahiana sy fiarovana',
  '7.14 – Fanomanana sy fampieritreretana',
  '7.15 – Ady masina sy fandresena',
  '7.16 – Fahafatesana – fitsanganana amin\'ny maty',
  '7.17 – Ny lanitra sy ny fiainana mandrakizay',
  '8.01 – Fampakaram-bady',
  '8.02 – Fanokanana trano',
  '8.03 – Amin\'ny maraina',
  '8.04 – Amin\'ny hariva',
  '8.05 – Amin\'ny sakafo',
  '8.06 – Ny handeha lavitra',
  '8.07 – Fivavahana ho an\'ny tanindranaza',
  '8.08 – Amin\'ny faran\'ny taona',
  '8.09 – Amin\'ny taom-baovao',
  'Fihirana Fanampiny',
  'Non classé'
];

const FihiranaList = () => {
  const { currentUser } = useAuth();
  const { data, addFihirana, updateFihirana, deleteFihirana } = useData();
  const { createNotification } = useNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]); // Catégories déroulées
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingFihirana, setEditingFihirana] = useState(null);
  const [selectedFihirana, setSelectedFihirana] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [form, setForm] = useState({
    number: '',
    title: '',
    category: FIHIRANA_CATEGORIES[0],
    lyrics: ''
  });
  const [errors, setErrors] = useState({});

  const canManage = hasPermission(currentUser, 'manage_fihirana') || hasPermission(currentUser, 'edit_all');

  // Fonction pour basculer l'état déroulé d'une catégorie
  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Grouper les Fihirana par catégorie
  const groupedFihirana = FIHIRANA_CATEGORIES.reduce((acc, category) => {
    const fihiranaInCategory = (data.fihirana || [])
      .filter(f => f.category === category)
      .filter(f => {
        if (!searchTerm) return true;
        return f.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               f.number?.toString().includes(searchTerm) ||
               f.lyrics?.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => (a.number || 0) - (b.number || 0));
    
    if (fihiranaInCategory.length > 0) {
      acc[category] = fihiranaInCategory;
    }
    return acc;
  }, {});

  // Statistiques
  const stats = {
    total: data.fihirana?.length || 0,
    categories: Object.keys(groupedFihirana).length
  };

  const handleOpenModal = (fihirana = null) => {
    if (fihirana) {
      setEditingFihirana(fihirana);
      setForm({
        number: fihirana.number || '',
        title: fihirana.title || '',
        category: fihirana.category || FIHIRANA_CATEGORIES[0],
        lyrics: fihirana.lyrics || ''
      });
    } else {
      setEditingFihirana(null);
      setForm({
        number: '',
        title: '',
        category: FIHIRANA_CATEGORIES[0],
        lyrics: ''
      });
    }
    setShowModal(true);
  };

  const handleViewFihirana = (fihirana) => {
    setSelectedFihirana(fihirana);
    setShowViewModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.number) newErrors.number = 'Le numéro est requis';
    if (!form.title.trim()) newErrors.title = 'Le titre est requis';
    if (!form.lyrics.trim()) newErrors.lyrics = 'Les paroles sont requises';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      const fihiranaData = {
        ...form,
        number: parseInt(form.number),
        favorites: editingFihirana?.favorites || 0,
        lastUsed: editingFihirana?.lastUsed || new Date().toISOString()
      };

      if (editingFihirana) {
        await updateFihirana(editingFihirana.id, fihiranaData);
      } else {
        const result = await addFihirana(fihiranaData);
        if (result.success) {
          // Notification pour nouveau fihirana
          await notifyNewFihirana(createNotification, fihiranaData, currentUser.id);
        }
      }
      setShowModal(false);
      setEditingFihirana(null);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce Fihirana ?')) {
      deleteFihirana(id);
    }
  };

  const toggleFavorite = (fihiranaId) => {
    if (favorites.includes(fihiranaId)) {
      setFavorites(favorites.filter(id => id !== fihiranaId));
    } else {
      setFavorites([...favorites, fihiranaId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bibliothèque Fihirana</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Cantiques et chants de louange</p>
        </div>
        {canManage && (
          <Button
            onClick={() => handleOpenModal(null)}
            icon={Plus}
          >
            Ajouter un Fihirana
          </Button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Music className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cantiques</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BookOpen className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Catégories</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.categories}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un cantique par titre, numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Liste des catégories avec cantiques déroulants */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {Object.keys(groupedFihirana).length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Aucun Fihirana trouvé</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm
                ? 'Essayez de modifier votre recherche'
                : 'Commencez par ajouter des cantiques'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {Object.entries(groupedFihirana).map(([category, fihiranaList]) => (
              <div key={category} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                {/* En-tête de catégorie */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`transform transition-transform ${expandedCategories.includes(category) ? 'rotate-90' : ''}`}>
                      <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {category}
                    </h3>
                  </div>
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium px-3 py-1 rounded-full">
                    {fihiranaList.length}
                  </span>
                </button>

                {/* Liste des cantiques (déroulante) */}
                {expandedCategories.includes(category) && (
                  <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {fihiranaList.map((fihirana) => (
                        <div
                          key={fihirana.id}
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer hover:border-blue-500 dark:hover:border-blue-400"
                          onClick={() => handleViewFihirana(fihirana)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shadow-md flex-shrink-0">
                                {fihirana.number}
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
                                  {fihirana.title}
                                </h3>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(fihirana.id);
                              }}
                              className={`ml-2 flex-shrink-0 ${
                                favorites.includes(fihirana.id)
                                  ? 'text-red-500 fill-current'
                                  : 'text-gray-400 dark:text-gray-500'
                              } hover:text-red-500 transition-colors`}
                            >
                              <Heart className="w-5 h-5" />
                            </button>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                            <span className="flex items-center">
                              <Heart className="w-3 h-3 mr-1" />
                              {fihirana.favorites || 0}
                            </span>
                            <span className="flex items-center">
                              <Eye className="w-3 h-3 mr-1" />
                              Voir
                            </span>
                          </div>

                          <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-3">
                            {fihirana.lyrics?.substring(0, 100)}...
                          </p>

                          {canManage && (
                            <div className="flex gap-2 mt-3 pt-3 border-t dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleOpenModal(fihirana)}
                                className="flex-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium transition-colors"
                              >
                                <Edit2 className="w-3 h-3 inline mr-1" />
                                Modifier
                              </button>
                              <button
                                onClick={() => handleDelete(fihirana.id)}
                                className="flex-1 text-xs text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium transition-colors"
                              >
                                <Trash2 className="w-3 h-3 inline mr-1" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal d'édition */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingFihirana(null);
          setErrors({});
        }}
        title={editingFihirana ? 'Modifier le Fihirana' : 'Ajouter un Fihirana'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Numéro *
            </label>
            <input
              type="number"
              name="number"
              value={form.number}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.number ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.number && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.number}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titre *
            </label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Ex: Jesoa ô Tompo ô"
              className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Catégorie
            </label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FIHIRANA_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Paroles *
            </label>
            <textarea
              name="lyrics"
              value={form.lyrics}
              onChange={handleChange}
              rows={10}
              placeholder="Entrez les paroles du cantique..."
              className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.lyrics ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.lyrics && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lyrics}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setEditingFihirana(null);
                setErrors({});
              }}
            >
              Annuler
            </Button>
            <Button type="submit">
              {editingFihirana ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de visualisation */}
      {showViewModal && selectedFihirana && (
        <Modal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedFihirana(null);
          }}
          title={`Fihirana n°${selectedFihirana.number}`}
          size="lg"
        >
          <FihiranaViewer
            fihirana={selectedFihirana}
            onClose={() => {
              setShowViewModal(false);
              setSelectedFihirana(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default FihiranaList;